import type { InitOptions, Resource } from 'i18next';
import type { NamespaceName } from '../types';
import { buildResourceConfig, type SharedInitOptions } from './config';

import common from '../locales/en/common.json';
import validation from '../locales/en/validation.json';
import errors from '../locales/en/errors.json';
import sports from '../locales/en/sports.json';
import geo from '../locales/en/geo.json';
import currency from '../locales/en/currency.json';
import dates from '../locales/en/dates.json';
import notifications from '../locales/en/notifications.json';
import units from '../locales/en/units.json';
import verification from '../locales/en/verification.json';
import venue from '../locales/en/venue.json';
import claim from '../locales/en/claim.json';
import venueEditor from '../locales/en/venue-editor.json';
import amenities from '../locales/en/amenities.json';
import staff from '../locales/en/staff.json';
import affiliation from '../locales/en/affiliation.json';
import auditLog from '../locales/en/audit-log.json';
import discovery from '../locales/en/discovery.json';
import booking from '../locales/en/booking.json';
import availability from '../locales/en/availability.json';
// F17 — Cancellation policy + refund-tier display copy.
import cancellation from '../locales/en/cancellation.json';
// F20 — Off-platform payment marking copy.
import payment from '../locales/en/payment.json';
// F21 — Per-venue payment configuration copy.
import paymentConfig from '../locales/en/payment-config.json';
// F23 — Variable commission engine copy.
import commission from '../locales/en/commission.json';
// F28 — Email notifications (Postmark): subject/preview/shell-footer/
// unsubscribe-landing/admin-suppressions/error-code copy.
import email from '../locales/en/email.json';
// F27 — WhatsApp Business API: per-template body text in code form (plain-text
// fallback + admin preview), variable placeholder names, footer copy, button
// labels. English-only at MVP; Phase 2 adds Arabic + French.
import whatsapp from '../locales/en/whatsapp.json';
// F29 — Team lifecycle (role / status / privacy / join_policy labels, action
// labels, audit action labels, error code copy, notification subjects/bodies,
// invite-flow copy, confirm-dialog copy, field labels).
import team from '../locales/en/team.json';
// F30 — Team Chat (Scoped) lifecycle copy. Action labels, empty-states,
// locked-room banner copy per locked_reason, soft-delete stub copy per
// deleted_by_role, system-event body templates, poll-expiry preset labels,
// 22 error code labels, read-by sheet labels, list-preview formatters.
import chat from '../locales/en/chat.json';
import skill from '../locales/en/skill.json';
// F32 — Open Games Marketplace. Status / approval-mode / report-reason labels,
// marketplace + filter copy, off-platform-price notice, out-of-range warning,
// join / leave / cancel copy, captain-inbox labels, error codes, notification
// subjects/bodies. English-only at MVP.
import openGame from '../locales/en/open-game.json';
// F33 — Trainer Marketplace. Marketplace + filter + sort labels, trainer-
// profile labels, package / certification labels + statuses, book-trainer
// flow copy (3 location modes + off-platform payment notice), onboarding
// wizard step labels, 10 error-code messages. English-only at MVP.
import trainer from '../locales/en/trainer.json';
// F35 — Trainer Billing Model. Plan-mode + comparison copy, switch/cancel
// copy, queued-switch + double-commission + settlement banners, trial copy,
// invoices list/detail copy, admin override dialog copy, 7 error codes.
import trainerBilling from '../locales/en/trainer-billing.json';
// F36 — Trainer-Managed Teams (Club archetype). Club dashboard + settings,
// team/trainer management, training-schedule editor, attendance tracker,
// friendly-match handshake, tournament-interest stub, 21 error codes, the 10
// club notification subjects/bodies. English-only at MVP.
import club from '../locales/en/club.json';
// F37 — Player ↔ Trainer Recommendations. Review + badge labels, star labels,
// seeded review-tag + badge display names, composer copy, edit-window banner,
// moderation action labels, 14 error codes, notification subjects/bodies.
import recommendation from '../locales/en/recommendation.json';
// F38 — B2B SaaS Subscription Tiers. Tier / status / cadence labels, invoice +
// off-platform-settlement copy, feature-gate upsell copy, the 9 subscription
// notification subjects/bodies, 12 error codes, audit-action labels. English-only at MVP.
import subscription from '../locales/en/subscription.json';
// F39 — Featured Placements. Placement-type + status labels, price-calculator copy,
// the 7 venue-owner notification subjects/bodies + the sponsored-ad template, the
// "Sponsored" badge label + disclosure, off-platform settlement copy, upgrade-prompt
// copy, scarcity sold-out copy, 17 error codes, audit-action labels, admin CMS labels.
// English-only at MVP.
import placement from '../locales/en/placement.json';
// F40 — Promotions. Status / display-mode / discount-type / scope labels, the
// evaluation-reason + error-code messages, action labels, the B2B creator wizard
// copy, the B2C promotions-tab copy, the admin moderation + analytics copy, and the
// 6 promotion notification subjects/bodies. English-only at MVP.
import promotion from '../locales/en/promotion.json';
// F41 — Tournament & Event Listings. Tournament / division / registration / host /
// organizer status labels, format / seeding / gender labels, action + audit-action
// labels, the 38 error-code messages, the 16 notification subjects/bodies, hosting-
// approval copy, organizer-approval copy, empty-states. English-only at MVP.
import tournament from '../locales/en/tournament.json';
// F43 — Admin User Management & Moderation. Report reason/status/subject labels,
// central queue + detail copy, chat-moderation banner, suspend/restore dialog
// copy, GDPR queue + SLA copy, bulk-action copy, the 8 error-code messages.
// English-only at MVP.
import moderation from '../locales/en/moderation.json';

export const SHARED_NAMESPACES: readonly NamespaceName[] = [
  'common',
  'validation',
  'errors',
  'sports',
  'geo',
  'currency',
  'dates',
  'notifications',
  'units',
  'venue',
  'verification',
  'claim',
  'venue-editor',
  'amenities',
  'staff',
  'affiliation',
  'audit-log',
  'discovery',
  'booking',
  'availability',
  // F17 — Cancellation policy namespace (used by cancel Dialog + venue-editor
  // policy tab + admin platform-default editor).
  'cancellation',
  // F20 — Off-platform payment marking namespace (method labels, action labels,
  // dialog copy, banner copy, instructions copy, errors, audit labels, dispute
  // outcomes).
  'payment',
  // F21 — Per-venue payment configuration namespace (form labels, billing-model
  // labels, admin-override banner copy, completeness checklist, validation
  // messages, audit-action labels, notification copy).
  'payment-config',
  // F23 — Variable commission engine namespace (status labels, action labels,
  // KPI labels, banner copy, change-rate / mark-settled / void dialog copy,
  // error codes, audit-action labels).
  'commission',
  // F28 — Email notifications namespace (subject lines, preview text, shell
  // footer, unsubscribe-landing-page copy, admin suppressions UI copy,
  // error-code labels). English-only at MVP; Phase 2 translation drop adds
  // Arabic + French.
  'email',
  // F27 — WhatsApp Business API namespace (per-template body text in code
  // form for plain-text fallback + admin preview, variable placeholder
  // names, footer copy, button labels). English-only at MVP; Phase 2
  // translation drop adds Arabic + French.
  'whatsapp',
  // F29 — Team lifecycle namespace (role / status / privacy / join_policy
  // labels, action labels, audit action labels, error code copy,
  // notification subjects/bodies, invite-flow copy, confirm-dialog copy,
  // field labels). English-only at MVP.
  'team',
  // F30 — Team Chat (Scoped) namespace. Action labels, empty-states,
  // locked-room banner copy, soft-delete stubs, 8 system-event body templates,
  // poll-expiry preset labels, 22 error codes, read-by sheet copy, list-preview
  // formatters. English-only at MVP.
  'chat',
  // F31 — Skill-Based Matching namespace. Level-band + confidence-tier labels,
  // outcome status/action labels, confirm/report/dispute dialog copy, rating-
  // delta + history-kind labels, admin dispute/descriptor/override copy, 10
  // error-code messages. English-only at MVP.
  'skill',
  // F32 — Open Games Marketplace namespace. Status / approval-mode /
  // report-reason labels, marketplace + filter copy, off-platform-price
  // notice, out-of-range warning, join / leave / cancel copy, captain-inbox
  // labels, ~14 error-code messages, notification subjects/bodies.
  // English-only at MVP.
  'open-game',
  // F33 — Trainer Marketplace namespace. Marketplace + filter + sort labels,
  // trainer-profile labels, package / certification labels + statuses,
  // book-trainer flow copy (3 location modes + off-platform payment notice),
  // onboarding wizard step labels, 10 error-code messages. English-only at MVP.
  'trainer',
  // F35 — Trainer Billing Model namespace.
  'trainer-billing',
  // F36 — Trainer-Managed Teams (Club archetype) namespace. Role/status/cadence/
  // attendance/friendly labels, action + audit-action labels, 21 error codes,
  // 10 notification subjects/bodies, empty-states. English-only at MVP.
  'club',
  // F37 — Player ↔ Trainer Recommendations namespace. Review + badge labels,
  // star labels, seeded tag + badge display names, composer copy, edit-window
  // banner, moderation action labels, 14 error codes, notification subjects/
  // bodies. English-only at MVP.
  'recommendation',
  // F38 — B2B SaaS Subscription Tiers namespace.
  'subscription',
  // F39 — Featured Placements namespace. Placement-type + status labels,
  // price-calculator copy, the 7 venue-owner notification subjects/bodies + the
  // sponsored-ad template, "Sponsored" badge label + disclosure, off-platform
  // settlement copy, upgrade-prompt copy, scarcity sold-out copy, 17 error codes,
  // audit-action labels, admin CMS labels. English-only at MVP.
  'placement',
  // F40 — Promotions namespace. Status / display-mode / discount-type / scope
  // labels, the evaluation-reason + error-code messages, action labels, the B2B
  // creator wizard copy, the B2C promotions-tab copy, the admin moderation +
  // analytics copy, and the 6 promotion notification subjects/bodies.
  // English-only at MVP.
  'promotion',
  // F41 — Tournament & Event Listings namespace. Tournament / division /
  // registration / host / organizer status labels, format / seeding / gender
  // labels, action + audit-action labels, 38 error codes, 16 notification
  // subjects/bodies, hosting-approval + organizer-approval copy, empty-states.
  // English-only at MVP.
  'tournament',
  // F43 — Admin User Management & Moderation. Report reason/status/subject labels,
  // central queue + detail copy, chat-moderation banner, suspend/restore dialog
  // copy, GDPR queue + SLA copy, bulk-action copy, the 8 error-code messages.
  'moderation',
] as const;

export const SHARED_RESOURCES = {
  en: {
    common,
    validation,
    errors,
    sports,
    geo,
    currency,
    dates,
    notifications,
    units,
    venue,
    verification,
    claim,
    'venue-editor': venueEditor,
    amenities,
    staff,
    affiliation,
    'audit-log': auditLog,
    discovery,
    booking,
    availability,
    cancellation,
    payment,
    'payment-config': paymentConfig,
    commission,
    email,
    whatsapp,
    team,
    chat,
    skill,
    'open-game': openGame,
    trainer,
    'trainer-billing': trainerBilling,
    club,
    recommendation,
    subscription,
    placement,
    promotion,
    tournament,
    moderation,
  },
} as const satisfies Resource;

export type { SharedInitOptions } from './config';

export function buildSharedConfig(opts: SharedInitOptions): InitOptions {
  return buildResourceConfig(opts, SHARED_NAMESPACES, SHARED_RESOURCES);
}
